import React, { useContext, useState, useEffect } from "react";
import { FirebaseContext } from "../contexts/FirebaseContext";
import LabelFilterStrip from "./LabelFilterStrip";
import ToggleSwitch from "./ToggleSwitch";
import MarkdownDescription from "./MarkdownDescription";
import { useManageLabels } from "../hooks/useManageLabels";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";
import { dueDateToInput, inputToDueDate } from "../utils/dueDate";
import styles from "../styles/TaskDetailContainerStyles";

const TASK_STATE_BACKLOG = "task_state_backlog";
const TASK_STATE_DONE = "task_state_done";
const TASK_STATE_WIP = "task_state_wip";

const TaskDetailContainer = ({
  task,
  taskName,
  taskDescription,
  handleNameChange,
  handleDescriptionChange,
  handleDeleteTask,
}) => {
  const taskStates = [
    { label: "To Do", value: TASK_STATE_BACKLOG },
    { label: "WIP", value: TASK_STATE_WIP },
    { label: "Done", value: TASK_STATE_DONE },
  ];
  const {
    projects,
    updateTask,
    setTaskState,
    setTaskTrackingState,
    labels,
    updateTaskLabels,
  } = useContext(FirebaseContext);

  // Same label + action-template management dialog as Home / Stats. Assigning
  // labels to this task stays inline (toggle the chips); editing/adding labels
  // and configuring action templates happens in the shared dialog.
  const openManageLabels = useManageLabels(
    task && task.projectId != null ? Number(task.projectId) : null
  );

  const selectedLabelIds = new Set((task.labelIds || []).map(Number));
  const toggleTaskLabel = (id) => {
    const ids = (task.labelIds || []).map(Number);
    const next = ids.includes(id)
      ? ids.filter((x) => x !== id)
      : [...ids, id];
    updateTaskLabels(task.id, next);
  };
  // Labels visible for this task: globals + this project's labels.
  const taskLabels = labels.filter(
    (l) => l.projectId == null || Number(l.projectId) === Number(task.projectId)
  );

  const [selectedProjectId, setSelectedProjectId] = useState(
    String(task.projectId)
  );

  useEffect(() => {
    setSelectedProjectId(String(task.projectId));
  }, [task.projectId]);

  const handleStateChange = async (newTaskState) => {
    if (newTaskState === task.taskState) return;
    if (newTaskState === TASK_STATE_DONE) {
      if (task.trackStartedTimestamp) {
        await setTaskTrackingState(task.id, task.projectId, false);
      }
      await updateTask(task.id, {
        taskState: TASK_STATE_DONE,
        taskBeingTracked: false,
        trackStartedTimestamp: null,
      });
    } else {
      setTaskState(task.id, newTaskState);
    }
  };

  const handleProjectChange = async (newProjectId) => {
    const projectId = isNaN(newProjectId)
      ? String(newProjectId)
      : Number(newProjectId);
    try {
      await updateTask(task.id, { projectId });
      setSelectedProjectId(projectId);
      console.log(`Task ${task.id} updated with new projectId: ${projectId}`);
    } catch (error) {
      console.error("Error updating task with new projectId:", error);
    }
  };

  return (
    <div>
      {/* Label strip — assign labels to this task; "Add Label" opens the dialog. */}
      {!task.usedAsNote && (
        <div style={{ marginBottom: "8px" }}>
          <LabelFilterStrip
            labels={taskLabels}
            selectedIds={selectedLabelIds}
            onToggle={toggleTaskLabel}
            onClearAll={() => {}}
            showAllChip={false}
            onAddLabel={openManageLabels}
          />
        </div>
      )}
      <div>
        <label htmlFor="taskName">Task Name:</label>
        <input
          type="text"
          id="taskName"
          value={taskName}
          onChange={handleNameChange}
          style={styles.input}
        />
      </div>

      <MarkdownDescription
        label="Task Description:"
        value={taskDescription}
        onChange={handleDescriptionChange}
        minHeight={160}
      />
      <div style={{ height: "8px" }} />

      {/* Task State — segmented pill control (matches the recurring detail
          "Create as" chips): selected state is filled blue. */}
      <div>
        <label>Task State:</label>
        <div style={styles.chipRow}>
          {taskStates.map((state) => (
            <button
              key={state.value}
              type="button"
              onClick={() => handleStateChange(state.value)}
              style={
                task.taskState === state.value ? styles.chipOn : styles.chip
              }
            >
              {state.label}
            </button>
          ))}
        </div>
      </div>

      {/* Due date (Android TaskDetail parity). Wire format: non-null Long,
          0 = no due date (never null), UTC-midnight millis of the chosen day —
          exactly what Android's Material3 picker stores. Clearing writes 0.
          Android has no clear control; web adds one since the input allows it. */}
      {!task.usedAsNote && (
        <div>
          <label htmlFor="taskDueDate">Due Date:</label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="date"
              id="taskDueDate"
              value={dueDateToInput(task.dueDate)}
              onChange={(e) =>
                updateTask(task.id, { dueDate: inputToDueDate(e.target.value) })
              }
              style={{ ...styles.select, colorScheme: "light dark" }}
            />
            {task.dueDate > 0 && (
              <button
                type="button"
                onClick={() => updateTask(task.id, { dueDate: 0 })}
                style={styles.chip}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="projectSelector">Project:</label>
        <select
          id="projectSelector"
          value={selectedProjectId}
          onChange={(e) => handleProjectChange(e.target.value)}
          style={styles.select}
        >
          {projects.map((project) => (
            <option key={project.id} value={String(project.id)}>
              {project.projectName}
            </option>
          ))}
        </select>
      </div>

      {/* Per-task AI tracking toggle ("Track AI on this task"). Only relevant
          when the global AI Invoice setting is on. Default on. */}
      {!task.usedAsNote && isAiInvoiceEnabled() && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            margin: "16px 0",
            color: "var(--text)",
          }}
        >
          <span style={{ flex: 1 }}>Track AI on this task</span>
          <ToggleSwitch
            checked={task.aiTrackingEnabled !== false}
            onChange={(next) => updateTask(task.id, { aiTrackingEnabled: next })}
          />
        </div>
      )}

      {/* Delete Button */}
      <button onClick={handleDeleteTask} style={styles.deleteButton}>
        Delete Task
      </button>
    </div>
  );
};

export default TaskDetailContainer;
